import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { 
  FiDownload, 
  FiCalendar, 
  FiFilter,
  FiTrendingUp,
  FiDollarSign,
  FiShoppingCart,
  FiArrowUpRight,
  FiArrowDownRight
} from 'react-icons/fi';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import toast from 'react-hot-toast';
import { fetchAnalytics, fetchAnalyticsSummary, setFilters, exportAnalytics } from '../../store/slices/analyticsSlice';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

export const AdminAnalyticsPage = () => {
  const dispatch = useDispatch();
  const { analytics, summary, isLoading, error, filters } = useSelector((state) => state.analytics);
  const [localFilters, setLocalFilters] = useState(filters);
  const [period, setPeriod] = useState('30');

  useEffect(() => {
    dispatch(fetchAnalyticsSummary(parseInt(period)));
  }, [dispatch, period]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    dispatch(setFilters(localFilters));
    dispatch(fetchAnalytics(localFilters));
  };

  const handleExport = (format) => {
    dispatch(exportAnalytics({ filters: localFilters, format }));
    if (format === 'csv') {
      toast.success('Analytics exported as CSV');
    }
  };

  const stats = analytics.stats || {};
  const chartData = {
    // Daily revenue line chart
    dailyRevenue: {
      labels: (summary.dailyRevenue || []).map(d => d._id),
      datasets: [
        {
          label: 'Daily Revenue (₦)',
          data: (summary.dailyRevenue || []).map(d => d.revenue),
          borderColor: 'rgb(79, 70, 229)',
          backgroundColor: 'rgba(79, 70, 229, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: 'rgb(79, 70, 229)',
        }
      ]
    },

    // Transaction types bar chart
    transactionTypes: {
      labels: (summary.transactionsByType || []).map(t => {
        const typeMap = { deposit: 'Deposits', purchase: 'Purchases', refund: 'Refunds' };
        return typeMap[t._id] || t._id;
      }),
      datasets: [
        {
          label: 'Count',
          data: (summary.transactionsByType || []).map(t => t.count),
          backgroundColor: 'rgba(79, 70, 229, 0.6)',
          borderColor: 'rgb(79, 70, 229)',
          borderWidth: 1
        },
        {
          label: 'Revenue (₦)',
          data: (summary.transactionsByType || []).map(t => t.revenue),
          backgroundColor: 'rgba(236, 72, 153, 0.6)',
          borderColor: 'rgb(236, 72, 153)',
          borderWidth: 1
        }
      ]
    },

    // Payment method pie chart
    paymentMethods: {
      labels: (summary.byPaymentMethod || []).map(m => 
        m._id.charAt(0).toUpperCase() + m._id.slice(1)
      ),
      datasets: [
        {
          data: (summary.byPaymentMethod || []).map(m => m.revenue),
          backgroundColor: [
            'rgba(79, 70, 229, 0.8)',
            'rgba(236, 72, 153, 0.8)',
            'rgba(34, 197, 94, 0.8)',
            'rgba(251, 146, 60, 0.8)'
          ]
        }
      ]
    }
  };

  const StatCard = ({ icon: Icon, label, value, change, trend }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-xl p-4"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-luxury-500 mb-2">{label}</p>
          <p className="text-2xl font-bold text-luxury-900">{value}</p>
          {change !== undefined && (
            <div className="flex items-center mt-2 text-sm">
              {trend === 'up' ? (
                <FiArrowUpRight className="text-green-500 mr-1" />
              ) : (
                <FiArrowDownRight className="text-red-500 mr-1" />
              )}
              <span className={trend === 'up' ? 'text-green-500' : 'text-red-500'}>
                {change}
              </span>
            </div>
          )}
        </div>
        <div className="p-3 bg-primary-100 rounded-lg">
          <Icon className="text-primary-600 text-xl" />
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-luxury-900">Analytics Dashboard</h1>
          <p className="text-luxury-500 mt-1">Platform statistics and performance metrics</p>
        </div>
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={() => handleExport('json')}
            className="btn-primary flex items-center gap-2"
          >
            <FiDownload /> Export JSON
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={() => handleExport('csv')}
            className="btn-primary flex items-center gap-2"
          >
            <FiDownload /> Export CSV
          </motion.button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Period Selector */}
      <div className="flex gap-2">
        {['7', '30', '90', '365'].map(days => (
          <motion.button
            key={days}
            whileHover={{ scale: 1.05 }}
            onClick={() => setPeriod(days)}
            className={`px-4 py-2 rounded-lg transition-all ${
              period === days
                ? 'bg-primary-600 text-white'
                : 'glass hover:bg-primary-50'
            }`}
          >
            {days === '7' ? 'Week' : days === '30' ? 'Month' : days === '90' ? '3 Months' : 'Year'}
          </motion.button>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={FiDollarSign}
          label="Total Revenue"
          value={`₦${(summary.totalRevenue || 0).toLocaleString()}`}
        />
        <StatCard
          icon={FiShoppingCart}
          label="Total Transactions"
          value={(stats.totalTransactions || 0).toLocaleString()}
        />
        <StatCard
          icon={FiTrendingUp}
          label="Success Rate"
          value={`${stats.successRate || 0}%`}
        />
        <StatCard
          icon={FiDollarSign}
          label="Avg Transaction"
          value={`₦${(stats.averageTransaction || 0).toLocaleString()}`}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Revenue */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-luxury-900 mb-4">Daily Revenue</h3>
          <div style={{ height: '300px' }}>
            <Line
              data={chartData.dailyRevenue}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false }
                },
                scales: {
                  y: { beginAtZero: true }
                }
              }}
            />
          </div>
        </motion.div>

        {/* Payment Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-luxury-900 mb-4">Revenue by Payment Method</h3>
          <div style={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Doughnut
              data={chartData.paymentMethods}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom' }
                }
              }}
            />
          </div>
        </motion.div>

        {/* Transaction Types */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-xl p-6 lg:col-span-2"
        >
          <h3 className="text-lg font-semibold text-luxury-900 mb-4">Transactions by Type</h3>
          <div style={{ height: '300px' }}>
            <Bar
              data={chartData.transactionTypes}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: { beginAtZero: true }
                }
              }}
            />
          </div>
        </motion.div>
      </div>

      {/* Detailed Analytics Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-xl p-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <FiFilter className="text-primary-600" />
          <h3 className="text-lg font-semibold text-luxury-900">Advanced Filters</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          {/* Date Range */}
          <div>
            <label className="text-sm font-medium text-luxury-700 mb-1 block">Start Date</label>
            <input
              type="date"
              name="startDate"
              value={localFilters.startDate || ''}
              onChange={handleFilterChange}
              className="input-luxury w-full"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-luxury-700 mb-1 block">End Date</label>
            <input
              type="date"
              name="endDate"
              value={localFilters.endDate || ''}
              onChange={handleFilterChange}
              className="input-luxury w-full"
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className="text-sm font-medium text-luxury-700 mb-1 block">Payment Method</label>
            <select
              name="paymentMethod"
              value={localFilters.paymentMethod}
              onChange={handleFilterChange}
              className="input-luxury w-full"
            >
              <option value="">All Methods</option>
              <option value="paystack">Paystack</option>
              <option value="korapay">Korapay</option>
              <option value="wallet">Wallet</option>
            </select>
          </div>

          {/* Transaction Type */}
          <div>
            <label className="text-sm font-medium text-luxury-700 mb-1 block">Type</label>
            <select
              name="transactionType"
              value={localFilters.transactionType}
              onChange={handleFilterChange}
              className="input-luxury w-full"
            >
              <option value="">All Types</option>
              <option value="deposit">Deposit</option>
              <option value="purchase">Purchase</option>
              <option value="refund">Refund</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="text-sm font-medium text-luxury-700 mb-1 block">Status</label>
            <select
              name="transactionStatus"
              value={localFilters.transactionStatus}
              onChange={handleFilterChange}
              className="input-luxury w-full"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          onClick={handleApplyFilters}
          className="btn-primary"
        >
          Apply Filters
        </motion.button>
      </motion.div>

      {/* Transactions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-xl overflow-hidden"
      >
        <div className="p-6 border-b border-luxury-200">
          <h3 className="text-lg font-semibold text-luxury-900">
            Transactions ({analytics.pagination?.totalCount || 0})
          </h3>
        </div>

        {isLoading ? (
          <div className="p-6 text-center">
            <div className="inline-block animate-spin">
              <FiTrendingUp className="text-primary-600 text-3xl" />
            </div>
          </div>
        ) : (analytics.transactions || []).length === 0 ? (
          <div className="p-6 text-center text-luxury-500">
            No transactions found. Try adjusting your filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-luxury-50 border-b border-luxury-200">
                <tr>
                  <th className="px-6 py-3 text-left font-medium text-luxury-700">Date</th>
                  <th className="px-6 py-3 text-left font-medium text-luxury-700">User</th>
                  <th className="px-6 py-3 text-left font-medium text-luxury-700">Type</th>
                  <th className="px-6 py-3 text-left font-medium text-luxury-700">Amount</th>
                  <th className="px-6 py-3 text-left font-medium text-luxury-700">Method</th>
                  <th className="px-6 py-3 text-left font-medium text-luxury-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {(analytics.transactions || []).map((tx) => (
                  <tr key={tx._id} className="border-b border-luxury-100 hover:bg-luxury-50">
                    <td className="px-6 py-3 text-luxury-900">
                      {new Date(tx.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-3 text-luxury-900">
                      {tx.user?.username || tx.user?.email || 'Unknown'}
                    </td>
                    <td className="px-6 py-3 capitalize text-luxury-700">
                      {tx.type}
                    </td>
                    <td className="px-6 py-3 font-medium text-luxury-900">
                      ₦{tx.amount?.toLocaleString()}
                    </td>
                    <td className="px-6 py-3 capitalize text-luxury-700">
                      {tx.paymentMethod}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        tx.status === 'success'
                          ? 'bg-green-100 text-green-700'
                          : tx.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {tx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {analytics.pagination?.totalPages > 1 && (
          <div className="p-6 border-t border-luxury-200 flex justify-center gap-2">
            {Array.from({ length: analytics.pagination.totalPages }, (_, i) => i + 1).map(page => (
              <motion.button
                key={page}
                whileHover={{ scale: 1.05 }}
                onClick={() => {
                  const newFilters = { ...localFilters, page };
                  setLocalFilters(newFilters);
                  dispatch(fetchAnalytics(newFilters));
                }}
                className={`px-3 py-1 rounded-lg transition-all ${
                  analytics.pagination.page === page
                    ? 'bg-primary-600 text-white'
                    : 'glass hover:bg-primary-50'
                }`}
              >
                {page}
              </motion.button>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};
